import { useTranslation } from "react-i18next";
import DataTable from "components/datatable/Datatable";
import Currency from "components/format/currency/currency";
import PrettyDate from "components/format/PrettyDate";
import { btnLinkCls } from "components/tw/classes";
import type {
  SalesRegisterMonth,
  SalesRegisterRow,
  SalesRegisterTotals,
} from "services/invoices.service";
import type { Column, FooterRow } from "types/Datatable";

type TFunction = (key: string, options?: Record<string, unknown>) => string;

/**
 * An amount. The table names its amounts in the column headers; a phone card has no header,
 * so the optional `label` is shown in front of the amount below md only.
 */
function Amount({
  value,
  bold = false,
  label,
}: {
  value: number;
  bold?: boolean;
  label?: string;
}) {
  return (
    <span
      className={`block text-right tabular-nums ${bold ? "font-semibold" : ""}`}
    >
      {label && (
        <span className="mr-1 text-xs font-normal text-zinc-500 md:hidden">
          {label}
        </span>
      )}
      <Currency value={value} isDecimal />
    </span>
  );
}

function buildRowColumns(
  t: TFunction,
  onView: (row: SalesRegisterRow) => void
): Column[] {
  const amountColumn = (
    key: "net" | "vat" | "gross",
    name: string
  ): Column => ({
    key,
    header: <span className="block text-right">{name}</span>,
    type: "custom",
    render: (row: SalesRegisterRow) => <Amount value={row[key]} label={name} />,
  });

  return [
    { key: "#", name: "#" },
    {
      key: "customerName",
      name: t("invoice_buyer"),
      type: "custom",
      mobileView: "topRight",
      render: (row: SalesRegisterRow) => {
        const codes = [
          row.customerCode &&
            t("sales_register_company_code", { code: row.customerCode }),
          row.customerVatCode &&
            t("sales_register_vat_code", { code: row.customerVatCode }),
        ]
          .filter(Boolean)
          .join(", ");
        return (
          <div className="max-md:pl-3 max-md:text-right max-md:[overflow-wrap:anywhere]">
            {row.customerName}
            {codes && <div className="text-xs text-zinc-500">{codes}</div>}
          </div>
        );
      },
    },
    {
      key: "number",
      name: t("invoice_inbox_col_invoice_number"),
      type: "custom",
      mobileView: "topLeft",
      // The number is a button for keyboard users and opens the invoice itself;
      // it stops the click so the row, which opens the same invoice, does not
      // open it a second time. Tall enough for a finger on the phone card.
      render: (row: SalesRegisterRow) => (
        <button
          type="button"
          className={`${btnLinkCls} whitespace-nowrap max-md:min-h-11`}
          onClick={(event) => {
            event.stopPropagation();
            onView(row);
          }}
        >
          {row.number}
        </button>
      ),
    },
    { key: "date", name: t("date"), type: "date", mobileView: "bottomLeft" },
    {
      key: "paymentDueDate",
      name: t("invoice_inbox_col_due"),
      type: "custom",
      render: (row: SalesRegisterRow) =>
        row.paymentDueDate ? (
          <PrettyDate dateString={row.paymentDueDate} />
        ) : (
          "—"
        ),
    },
    amountColumn("net", t("net")),
    amountColumn("vat", t("vat")),
    { ...amountColumn("gross", t("total")), mobileView: "bottomRight" },
    { key: "currency", name: t("currency") },
    {
      key: "pvmCodes",
      name: t("sales_register_vat_classifier"),
      type: "custom",
      render: (row: SalesRegisterRow) => row.pvmCodes.join(", ") || "—",
    },
  ];
}

// The month's totals per currency, placed under the amount columns they add up. On a phone the
// totals are a box of bare cells, so each amount carries its name and the currency (already in
// the row label) is left out.
function buildTotalRows(
  t: TFunction,
  totals: SalesRegisterTotals[]
): FooterRow[] {
  return totals.map((total) => ({
    key: total.currency,
    label: `${t("total")} ${total.currency}`,
    cells: {
      net: <Amount value={total.net} label={t("net")} />,
      vat: <Amount value={total.vat} label={t("vat")} />,
      gross: <Amount value={total.gross} bold label={t("total")} />,
      currency: <span className="max-md:hidden">{total.currency}</span>,
    },
  }));
}

/** A month's invoices with its totals per currency under the amount columns. */
export default function SalesRegisterMonthTable({
  month,
  onView,
}: {
  month: SalesRegisterMonth;
  onView: (row: SalesRegisterRow) => void;
}) {
  const { t } = useTranslation();
  return (
    <DataTable
      collection={month.rows}
      columns={buildRowColumns(t, onView)}
      onRowClick={onView}
      isTotalsDisabled
      footerRows={buildTotalRows(t, month.totals)}
    />
  );
}
